'use client';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Search, X, Loader2, ChevronRight, ShoppingCart, Check } from 'lucide-react';
import { useRouter } from 'next/navigation';

const CART_STORAGE_KEY = 'plciraq_quote_cart';

interface CartItem {
  mpn: string;
  brand?: string;
  description?: string;
  quantity: number;
}

interface FallbackResult {
  mpn: string;
  brand: string;
  description: string;
  datasheet_url: string | null;
  image_url: string | null;
  source: 'local' | 'digikey' | 'mouser';
  stock_status?: string | null;
  local_id?: string | null;
  external_url?: string | null;
}

interface SearchBarProps {
  language?: 'en' | 'ar';
  placeholder?: string;
}

function StockBadge({ status }: { status?: string | null }) {
  if (status === 'in_stock') {
    return (
      <span className="flex-shrink-0 inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-600 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full whitespace-nowrap">
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
        In Stock
      </span>
    );
  }
  if (status === 'backorder') {
    return (
      <span className="flex-shrink-0 inline-flex items-center gap-1 text-[10px] font-semibold text-amber-600 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full whitespace-nowrap">
        <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
        Backorder
      </span>
    );
  }
  return null;
}

export default function SearchBar({ language = 'en', placeholder }: SearchBarProps) {
  const router = useRouter();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<FallbackResult[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [notFound, setNotFound] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Track which MPNs have just been added (for 2-second confirmation)
  const [addedMpns, setAddedMpns] = useState<Set<string>>(new Set());

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const defaultPlaceholder =
    placeholder ||
    (language === 'ar' ?'ابحث برقم القطعة (مثال: 6ES7214-1AG40)...' :'Search by Part Number (e.g., 6ES7214-1AG40)...');

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearch = async () => {
    const trimmed = query.trim();
    if (!trimmed) return;

    setIsLoading(true);
    setError(null);
    setResults([]);
    setTotalCount(0);
    setNotFound(false);
    setIsOpen(true);

    try {
      const res = await fetch('/api/search/fallback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mpn: trimmed }),
      });

      if (res.status === 404) {
        setNotFound(true);
        return;
      }

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data?.error ?? 'Search failed. Please try again.');
        return;
      }

      const data = await res.json();

      if (Array.isArray(data)) {
        if (data.length === 0) {
          setNotFound(true);
        } else {
          setResults(data.slice(0, 10));
          setTotalCount(data.length);
        }
      } else if (data && data.mpn) {
        setResults([data]);
        setTotalCount(1);
      } else {
        setNotFound(true);
      }
    } catch (err) {
      console.error('[SearchBar] Fetch error:', err);
      setError('Search unavailable. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleClear = () => {
    setQuery('');
    setResults([]);
    setTotalCount(0);
    setNotFound(false);
    setError(null);
    setIsOpen(false);
    setAddedMpns(new Set());
    inputRef.current?.focus();
  };

  const handleResultClick = (res: FallbackResult) => {
    setIsOpen(false);
    if (res.source === 'local') {
      router.push(`/products/${encodeURIComponent(res.mpn)}`);
    } else {
      const url = res.external_url || res.datasheet_url || null;
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        router.push(`/products/${encodeURIComponent(res.mpn)}`);
      }
    }
  };

  const handleAddToQuote = useCallback(
    (e: React.MouseEvent, res: FallbackResult) => {
      e.stopPropagation();

      try {
        const stored = localStorage.getItem(CART_STORAGE_KEY);
        const cart: CartItem[] = stored ? JSON.parse(stored) : [];

        const existing = cart.find((item) => item.mpn === res.mpn);
        if (existing) {
          existing.quantity += 1;
        } else {
          cart.push({
            mpn: res.mpn,
            brand: res.brand || undefined,
            description: res.description || undefined,
            quantity: 1,
          });
        }

        localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cart));

        // Show "Added ✓" for 2 seconds
        setAddedMpns((prev) => new Set(prev).add(res.mpn));
        setTimeout(() => {
          setAddedMpns((prev) => {
            const next = new Set(prev);
            next.delete(res.mpn);
            return next;
          });
        }, 2000);
      } catch (err) {
        console.error('[SearchBar] Failed to add to quote cart:', err);
      }
    },
    []
  );

  const handleViewAll = () => {
    setIsOpen(false);
    router.push(`/products?search=${encodeURIComponent(query.trim())}`);
  };

  const showDropdown = isOpen && (isLoading || results.length > 0 || notFound || error !== null);

  return (
    <div ref={containerRef} className="relative flex-1 w-full" dir={language === 'ar' ? 'rtl' : 'ltr'}>
      {/* Input */}
      <div className="relative flex items-center">
        <Search
          size={18}
          className="absolute left-3 text-slate-400 pointer-events-none z-10 flex-shrink-0"
          aria-hidden="true"
        />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => (results.length > 0 || notFound || error) && setIsOpen(true)}
          placeholder={defaultPlaceholder}
          className="w-full pl-10 pr-10 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#CE1126]/30 focus:border-[#CE1126] transition-all duration-200 bg-[#f8f9fa] font-roboto"
          aria-label="Search parts by MPN"
          aria-autocomplete="list"
          aria-expanded={showDropdown}
          autoComplete="off"
        />
        {/* Right icon: loader or clear */}
        <div className="absolute right-3 flex items-center">
          {isLoading ? (
            <Loader2 size={16} className="text-slate-400 animate-spin" aria-label="Searching..." />
          ) : query ? (
            <button
              type="button"
              onClick={handleClear}
              className="text-slate-400 hover:text-slate-600 transition-colors"
              aria-label="Clear search"
            >
              <X size={16} />
            </button>
          ) : null}
        </div>
      </div>

      {/* Dropdown */}
      {showDropdown && (
        <div
          className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-xl z-50 overflow-hidden"
          role="listbox"
          aria-label="Search results"
        >
          {/* Loading state */}
          {isLoading && (
            <div className="flex items-center justify-center gap-2 px-4 py-4 text-sm text-slate-500">
              <Loader2 size={16} className="animate-spin text-[#CE1126]" />
              <span>Searching…</span>
            </div>
          )}

          {/* Error state */}
          {!isLoading && error && (
            <div className="px-4 py-3 text-sm text-red-500 text-center">{error}</div>
          )}

          {/* No results */}
          {!isLoading && notFound && (
            <div className="px-4 py-4 text-center">
              <p className="text-sm text-slate-500">
                {language === 'ar'
                  ? `لا توجد نتائج لـ "${query}"`
                  : `No results found for "${query}"`}
              </p>
              <p className="text-xs text-slate-400 mt-1">
                {language === 'ar' ?'تواصل معنا عبر واتساب للاستفسار' :'Contact us via WhatsApp for special requests'}
              </p>
            </div>
          )}

          {/* Results list */}
          {!isLoading && results.length > 0 && (
            <>
              <div className="max-h-96 overflow-y-auto divide-y divide-slate-100">
                {results.map((res, idx) => {
                  const isAdded = addedMpns.has(res.mpn);
                  return (
                    <div
                      key={`${res.mpn}-${idx}`}
                      role="option"
                      className="flex items-center gap-2 px-3 py-2.5 hover:bg-slate-50 transition-colors"
                    >
                      {/* Product info — clickable, navigates to product page */}
                      <button
                        type="button"
                        onClick={() => handleResultClick(res)}
                        className="flex-1 min-w-0 text-left focus:outline-none focus:bg-slate-50"
                        aria-label={`View details for ${res.mpn}`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            {/* MPN */}
                            <span className="font-mono text-sm font-bold text-[#121212] block truncate">
                              {res.mpn}
                            </span>
                            {/* Brand */}
                            {res.brand && (
                              <span className="text-xs font-medium text-[#CE1126] block mt-0.5">
                                {res.brand}
                              </span>
                            )}
                            {/* Description */}
                            {res.description && (
                              <span className="text-xs text-slate-500 block truncate mt-0.5 max-w-xs">
                                {res.description.length > 70
                                  ? res.description.slice(0, 70) + '…'
                                  : res.description}
                              </span>
                            )}
                          </div>
                          {/* Stock badge */}
                          <div className="flex flex-col items-end gap-1 flex-shrink-0">
                            <StockBadge status={res.stock_status} />
                          </div>
                        </div>
                      </button>

                      {/* Add to Quote button */}
                      <button
                        type="button"
                        onClick={(e) => handleAddToQuote(e, res)}
                        className={`flex-shrink-0 flex items-center gap-1 text-xs font-medium px-2 py-1 rounded border transition-all duration-200 whitespace-nowrap ${
                          isAdded
                            ? 'bg-emerald-50 border-emerald-300 text-emerald-600' :'bg-white border-slate-300 text-slate-600 hover:bg-[#CE1126] hover:border-[#CE1126] hover:text-white'
                        }`}
                        aria-label={isAdded ? `${res.mpn} added to quote` : `Add ${res.mpn} to quote`}
                      >
                        {isAdded ? (
                          <>
                            <Check size={12} />
                            <span>Added</span>
                          </>
                        ) : (
                          <>
                            <ShoppingCart size={12} />
                            <span>+ Quote</span>
                          </>
                        )}
                      </button>
                    </div>
                  );
                })}
              </div>

              {/* View All link — shown when there are more than 10 matches */}
              {totalCount > 10 && (
                <button
                  type="button"
                  onClick={handleViewAll}
                  className="w-full flex items-center justify-between px-4 py-2.5 bg-slate-50 hover:bg-slate-100 border-t border-slate-200 text-xs font-medium text-[#CE1126] transition-colors"
                >
                  <span>
                    {language === 'ar'
                      ? `عرض جميع ${totalCount} نتيجة`
                      : `View All ${totalCount} Results`}
                  </span>
                  <ChevronRight size={14} />
                </button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
